import * as React from "react";
import { DropDown } from "./DropDown";
import { DropDownItem } from "./DropDown/DropDown";
import { LexicalEditor, $getSelection, $isRangeSelection } from "lexical";
import {
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from "@lexical/list";
import {
  BulletCircle,
  BulletDefaultIcon,
  BulletListIcon,
  BulletSquare,
} from "../../../assets/icons/SvgIcons";
import { bulletTypeToBlockName } from "../Helper";
import { ListTypeProps, toolTipConstants } from "../LexicalEditor.types";

export const rootTypeToRootName = {
  root: "Root",
  table: "Table",
};

export function BulletList({
  editor,
  bulletType,
  rootType,
  disabled = false,
  setBulletType,
  listType,
  setListType,
}: {
  bulletType: keyof typeof bulletTypeToBlockName;
  rootType: keyof typeof rootTypeToRootName;
  editor: LexicalEditor;
  disabled?: boolean;
  setBulletType: (type: string) => void;
  listType: ListTypeProps;
  setListType: (type: ListTypeProps) => void;
}): JSX.Element {
  type BulletType = keyof typeof bulletTypeToBlockName;

  const formatBulletList = (type: BulletType) => {
    // Only insert unordered list if not already a bullet list
    if (listType !== "bullet") {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
      setListType("bullet");
    }
    // Always set the bullet type and style
    setTimeout(() => {
      editor.update(() => {
        const selection = editor.getEditorState().read(() => $getSelection());
        if ($isRangeSelection(selection)) {
          const anchorNode = selection.anchor.getNode();
          const element = anchorNode.getTopLevelElementOrThrow();
          const elementDOM = editor.getElementByKey(element.getKey());
          if (elementDOM && elementDOM.tagName.toLowerCase() === "ul") {
            // Only set style if it's a UL
            switch (type) {
              case "circle":
                elementDOM.style.listStyleType = "circle";
                break;
              case "square":
                elementDOM.style.listStyleType = "square";
                break;
              default:
                elementDOM.style.listStyleType = "disc";
            }
          }
        }
      });
    }, 0);
    // Always update bulletType state
    setBulletType(type);
  };

  const handleButtonClick = () => {
    if (listType === "bullet") {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
      setListType(null);
    } else {
      // Use the current bulletType when toggling on
      formatBulletList(bulletType as BulletType);
    }
  };

  const bulletTypes: { type: BulletType; Icon: React.ComponentType }[] = [
    { type: "bullet", Icon: BulletListIcon },
    { type: "circle", Icon: BulletCircle },
    { type: "square", Icon: BulletSquare },
  ];

  function getBulletTypeFromDOM(elementDOM: HTMLElement): BulletType {
    switch (elementDOM.style.listStyleType) {
      case "circle":
        return "circle";
      case "square":
        return "square";
      default:
        return "bullet";
    }
  }
  const getBulletListTitle = (type: BulletType) => {
    switch (type) {
      case "bullet":
        return toolTipConstants.default;
      case "circle":
        return toolTipConstants.circle;
      case "square":
        return toolTipConstants.square;
    }
  };

  return (
    <DropDown
      disabled={disabled}
      buttonClassName="lexical-toolbarItem"
      buttonAriaLabel="Formatting options for text style"
      onClickContent={handleButtonClick}
      active={listType === "bullet"}
      dropdownItemClass="lexical-dropdownList"
      ButtonIcon={BulletDefaultIcon}
      dropdownIcon
      iconClassName="lexical-iconList2"
      title={toolTipConstants.bulletList}
    >
      {bulletTypes.map(({ type, Icon }) => (
        <DropDownItem
          key={type}
          className={`lexical-toolbarItem ${bulletType === type ? "lexical-itemActive" : ""
            }`}
          onClick={() => formatBulletList(type)}
          title={getBulletListTitle(type)}
        >
          <Icon />
        </DropDownItem>
      ))}
    </DropDown>
  );
}
